import type {
  ClientErrorReporterConfig,
  ErrorReportPayload,
  BrowserContext,
  NavigationEntry,
  NetworkRequestEntry,
  ConsoleEntry,
  PerformanceContext,
} from '../types';

/**
 * Клиентский (browser) error reporter.
 * Собирает максимум контекста: navigation history, network requests, console log, browser info.
 * Отправляет на backend endpoint через POST.
 */
export class ClientErrorReporter {
  private readonly config: ClientErrorReporterConfig;
  private navigationHistory: NavigationEntry[] = [];
  private networkRequests: NetworkRequestEntry[] = [];
  private consoleHistory: ConsoleEntry[] = [];
  private attached = false;

  private readonly maxNavigation: number;
  private readonly maxConsole: number;
  private readonly maxNetwork: number;

  constructor(config: ClientErrorReporterConfig) {
    this.config = config;
    this.maxNavigation = config.maxNavigationHistory ?? 10;
    this.maxConsole = config.maxConsoleHistory ?? 20;
    this.maxNetwork = config.maxNetworkRequests ?? 10;
  }

  /**
   * Отправить отчёт об ошибке на backend.
   */
  async report(error: Error | ErrorReportPayload, extra?: Partial<ErrorReportPayload>): Promise<void> {
    try {
      const payload = this.buildPayload(error, extra);
      const headers = typeof this.config.headers === 'function'
        ? this.config.headers()
        : this.config.headers ?? {};

      // Попытка через fetch, fallback на sendBeacon
      try {
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify(payload),
          credentials: 'include',
          keepalive: true,
        });
      } catch {
        // Fallback для unload сценариев
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(
            this.config.endpoint,
            new Blob([JSON.stringify(payload)], { type: 'application/json' }),
          );
        }
      }
    } catch {
      // Silently fail — нельзя бросать исключения из error reporter
    }
  }

  /**
   * Подключить глобальные обработчики ошибок и collectors.
   * Вызывать один раз при инициализации приложения.
   */
  attachGlobalHandlers(): void {
    if (this.attached || typeof window === 'undefined') return;
    this.attached = true;

    // Global error handler
    window.addEventListener('error', (event) => {
      // ResizeObserver loop — безвредное браузерное предупреждение, не репортим
      if (event.message?.includes('ResizeObserver loop')) return;

      if (event.error instanceof Error) {
        void this.report(event.error);
      } else {
        void this.report({
          message: event.message || 'Unknown error',
          stack: `at ${event.filename}:${event.lineno}:${event.colno}`,
          errorType: 'WindowError',
        });
      }
    });

    // Unhandled promise rejection
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason instanceof Error
        ? event.reason
        : { message: String(event.reason), errorType: 'UnhandledRejection' } satisfies ErrorReportPayload;
      void this.report(error);
    });

    // Navigation history collector
    this.patchNavigation();

    // Console collector
    this.patchConsole();

    // Network request collector
    this.patchFetch();
  }

  // ─── Payload Builder ───

  private buildPayload(error: Error | ErrorReportPayload, extra?: Partial<ErrorReportPayload>): ErrorReportPayload {
    const base: ErrorReportPayload = error instanceof Error
      ? {
          message: error.message,
          stack: error.stack?.slice(0, 10000),
          errorType: error.constructor.name,
        }
      : {
          ...error,
          message: error.message,
          stack: error.stack?.slice(0, 10000),
        };

    const user = this.config.getUser?.();

    return {
      ...base,
      ...extra,
      appName: this.config.appName,
      environment: this.config.environment,
      timestamp: new Date().toISOString(),
      user: extra?.user ?? user,
      browser: this.collectBrowserContext(),
      navigationHistory: [...this.navigationHistory],
      networkRequests: [...this.networkRequests],
      consoleHistory: [...this.consoleHistory],
      performance: this.collectPerformance(),
    };
  }

  // ─── Context Collectors ───

  private collectBrowserContext(): BrowserContext {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const conn = nav ? (nav as NavigatorWithConnection).connection : undefined;
    const perf = typeof performance !== 'undefined' ? performance : undefined;
    const mem = (perf as PerformanceWithMemory)?.memory;

    return {
      userAgent: nav?.userAgent ?? 'unknown',
      platform: nav?.platform ?? 'unknown',
      language: nav?.language ?? 'unknown',
      viewport: {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
      },
      devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
      connectionType: conn?.effectiveType,
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      title: typeof document !== 'undefined' ? document.title : undefined,
      memoryMB: mem ? mem.usedJSHeapSize / 1024 / 1024 : undefined,
    };
  }

  private collectPerformance(): PerformanceContext | undefined {
    if (typeof performance === 'undefined') return undefined;

    const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    const conn = (navigator as NavigatorWithConnection).connection;

    return {
      pageLoadMs: nav?.loadEventEnd ? nav.loadEventEnd - nav.startTime : undefined,
      domContentLoadedMs: nav?.domContentLoadedEventEnd ? nav.domContentLoadedEventEnd - nav.startTime : undefined,
      connectionType: conn?.type,
      effectiveType: conn?.effectiveType,
      downlinkMbps: conn?.downlink,
    };
  }

  // ─── Patchers ───

  private patchNavigation(): void {
    if (typeof window === 'undefined') return;

    // Запись текущего URL
    this.pushNavEntry(window.location.href);

    // Патч pushState
    const originalPushState = history.pushState.bind(history);
    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      originalPushState(...args);
      this.pushNavEntry(window.location.href);
    };

    // Патч replaceState
    const originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      originalReplaceState(...args);
      this.pushNavEntry(window.location.href);
    };

    // popstate (browser back/forward)
    window.addEventListener('popstate', () => {
      this.pushNavEntry(window.location.href);
    });
  }

  private pushNavEntry(url: string): void {
    this.navigationHistory.push({
      url,
      timestamp: new Date().toISOString(),
      title: typeof document !== 'undefined' ? document.title : undefined,
    });
    if (this.navigationHistory.length > this.maxNavigation) {
      this.navigationHistory.shift();
    }
  }

  private patchConsole(): void {
    if (typeof console === 'undefined') return;

    const originalError = console.error.bind(console);
    const originalWarn = console.warn.bind(console);

    console.error = (...args: unknown[]) => {
      this.pushConsoleEntry('error', args);
      originalError(...args);
    };

    console.warn = (...args: unknown[]) => {
      this.pushConsoleEntry('warn', args);
      originalWarn(...args);
    };
  }

  private pushConsoleEntry(level: 'error' | 'warn', args: unknown[]): void {
    const message = args.map((a) => {
      if (typeof a === 'string') return a;
      try { return JSON.stringify(a); } catch { return String(a); }
    }).join(' ').slice(0, 1000);

    this.consoleHistory.push({
      level,
      message,
      timestamp: new Date().toISOString(),
    });
    if (this.consoleHistory.length > this.maxConsole) {
      this.consoleHistory.shift();
    }
  }

  private patchFetch(): void {
    if (typeof window === 'undefined' || typeof fetch === 'undefined') return;

    const originalFetch = window.fetch.bind(window);

    const patchedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method ?? 'GET';
      const startTime = Date.now();

      try {
        const response = await originalFetch(input, init);
        this.pushNetworkEntry(url, method, response.status, Date.now() - startTime);
        return response;
      } catch (err) {
        this.pushNetworkEntry(url, method, 0, Date.now() - startTime);
        throw err;
      }
    };

    // Assign as fetch — cast needed because TypeScript's fetch type includes static methods
    window.fetch = patchedFetch as typeof window.fetch;
  }

  private pushNetworkEntry(url: string, method: string, status: number, durationMs: number): void {
    // Не записываем запросы к самому error-reporter endpoint (избегаем рекурсии)
    if (url.includes('/error-reports')) return;

    this.networkRequests.push({
      url: url.slice(0, 500),
      method: method.toUpperCase(),
      status: status || undefined,
      durationMs,
      timestamp: new Date().toISOString(),
    });
    if (this.networkRequests.length > this.maxNetwork) {
      this.networkRequests.shift();
    }
  }
}

// ─── Navigator type extensions ───

interface NetworkInformation {
  type?: string;
  effectiveType?: string;
  downlink?: number;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
}

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}
